import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import Tooltip from '../../../../../components/Tooltip';
import { addDays, addMonths, format } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { collection, doc, getDocs, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiCheckCircle, FiMail, FiRepeat, FiSlash, FiX } from 'react-icons/fi';
import firebaseFirestore from '~/config/firebase/firestore';
import { Location } from '~/core/types';
import { describirActor, registrarEvento } from '~/core/tracking/reservationEvents';
import { useAuth } from '~/hooks/useAuth';
import { crearSerie, FechaOcupadaError, FechaPlanificada } from './crearSerie';
import {
  Alternativa,
  claveFecha,
  enumerarDias,
  EstadoFecha,
  evaluarFecha,
  fechasDeLaSerie,
  franjasQueOcupa,
  MAXIMO_FECHAS,
  MAXIMO_MESES,
  NOMBRES_DIAS,
  diaDeLaSemana,
  salasAlternativas,
  Veredicto,
} from './serie';

/**
 * "Esta reserva se repite los miércoles y jueves hasta el 30 de noviembre."
 *
 * Se repite una reserva que YA existe en vez de pedir los datos otra vez: los
 * campos del cliente los define cada negocio en su formulario, así que
 * copiarlos de la reserva original es lo único que garantiza que coincidan.
 *
 * Dos pasos, y el segundo es el que importa: antes de crear nada se enseña
 * fecha por fecha qué va a pasar, y cada choque se resuelve a mano —cambiando
 * de sala solo ese día u omitiéndolo—. Nada se crea a espaldas de nadie.
 */

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** La reserva de la que se parte, tal cual viene de Firestore. */
  reserva: any;
  /**
   * Código que la reserva tiene apartado pero aún sin escribir, porque nació
   * marcada como repetida y su correo espera a que se cierre la serie.
   */
  codigoPendiente?: string;
  onCreadas?: () => void;
};

type Decision = { accion: 'crear' | 'omitir'; locationId: string };

type FilaFecha = {
  clave: string;
  fecha: Date;
  veredicto: Veredicto;
  alternativas: Alternativa[];
};

const COLOR_ESTADO: Record<EstadoFecha, string> = {
  libre: 'green',
  ocupada: 'orange',
  aforo: 'orange',
  bloqueada: 'purple',
  cerrada: 'gray',
};

const ETIQUETA_ESTADO: Record<EstadoFecha, string> = {
  libre: 'Disponible',
  ocupada: 'Ocupada',
  aforo: 'Sin cupo',
  bloqueada: 'Bloqueada',
  cerrada: 'Cerrada',
};

const capitalizar = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);

const fechaLarga = (f: Date) => capitalizar(format(f, "EEEE d 'de' MMMM", { locale: esLocale }));

/* ─────────────────────────  una fecha de la lista  ───────────────────────── */

const FilaRevision = ({
  fila,
  decision,
  tocada,
  onDecidir,
  salaBaseId,
}: {
  fila: FilaFecha;
  decision: Decision;
  /** Si nadie la ha tocado, sigue pendiente aunque por defecto vaya a omitirse. */
  tocada: boolean;
  onDecidir: (d: Decision) => void;
  /** La sala de la reserva original: a la que vuelve una fecha libre al incluirla otra vez. */
  salaBaseId: string;
}) => {
  const { veredicto } = fila;
  const libre = veredicto.estado === 'libre';
  const omitida = decision.accion === 'omitir';
  const movida = !libre && !omitida && Boolean(decision.locationId);
  // Una fecha libre que se quitó a mano: el cliente no la quiere ese día.
  const quitada = libre && omitida;
  // Una fecha sin decidir se queda en ámbar: apagarla haría creer que ya está
  // resuelta cuando en realidad se va a perder ese día.
  const pendiente = !libre && !tocada;
  const salaElegida = movida
    ? fila.alternativas.find((a) => a.location.id === decision.locationId)?.location.name
    : '';

  return (
    <Flex
      align='center'
      gap={3}
      py={2}
      px={3}
      borderBottomWidth='1px'
      borderColor='gray.100'
      opacity={omitida && tocada ? 0.55 : 1}
      bg={libre ? 'transparent' : movida ? 'green.50' : pendiente ? 'orange.50' : 'gray.50'}
    >
      <Icon
        as={movida ? FiCheckCircle : omitida && tocada ? FiSlash : libre ? FiCheckCircle : FiAlertTriangle}
        color={movida || libre ? 'green.500' : omitida && tocada ? 'gray.400' : 'orange.500'}
        boxSize={4}
        flexShrink={0}
      />

      <Box flex={1} minW={0}>
        <Text
          fontSize='sm'
          fontWeight={libre ? 'normal' : 'medium'}
          noOfLines={1}
          textDecoration={quitada ? 'line-through' : undefined}
        >
          {fechaLarga(fila.fecha)}
        </Text>
        {!libre && (
          <Text fontSize='xs' color={movida ? 'green.700' : 'gray.600'} noOfLines={1}>
            {movida ? `Se moverá a ${salaElegida}` : veredicto.motivo}
          </Text>
        )}
        {quitada && (
          <Text fontSize='xs' color='gray.600' noOfLines={1}>
            No se creará
          </Text>
        )}
      </Box>

      {libre ? (
        // Una fecha libre se puede quitar: a veces el cliente no quiere todas
        // las semanas seguidas. Se puede volver a incluir sin salir de aquí.
        <HStack spacing={1.5} flexShrink={0}>
          {quitada ? (
            <>
              <Badge colorScheme='gray' fontSize='0.65em'>
                Quitada
              </Badge>
              <Button
                size='xs'
                variant='link'
                colorScheme='blue'
                onClick={() => onDecidir({ accion: 'crear', locationId: salaBaseId })}
                aria-label={`Volver a incluir ${fechaLarga(fila.fecha)}`}
              >
                Incluir
              </Button>
            </>
          ) : (
            <>
              <Badge colorScheme='green' fontSize='0.65em'>
                {ETIQUETA_ESTADO.libre}
              </Badge>
              <Tooltip hasArrow fontSize='xs' label='Quitar esta fecha: no se creará'>
                <IconButton
                  size='xs'
                  variant='ghost'
                  color='gray.400'
                  _hover={{ color: 'red.500', bg: 'red.50' }}
                  icon={<FiX />}
                  aria-label={`Quitar ${fechaLarga(fila.fecha)}`}
                  onClick={() => onDecidir({ accion: 'omitir', locationId: '' })}
                />
              </Tooltip>
            </>
          )}
        </HStack>
      ) : (
        <HStack spacing={2} flexShrink={0}>
          {fila.alternativas.length > 0 ? (
            <Select
              size='xs'
              w='170px'
              borderRadius='md'
              bg='white'
              value={omitida ? '' : decision.locationId}
              onChange={(e) =>
                onDecidir(
                  e.target.value
                    ? { accion: 'crear', locationId: e.target.value }
                    : { accion: 'omitir', locationId: '' }
                )
              }
            >
              <option value=''>Omitir este día</option>
              <optgroup label='Mover a otra sala'>
                {fila.alternativas.map(({ location }) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </optgroup>
            </Select>
          ) : (
            <Tooltip
              hasArrow
              fontSize='xs'
              label='Ninguna otra sala está libre ese día a esa hora'
            >
              <Badge colorScheme={COLOR_ESTADO[veredicto.estado]} fontSize='0.65em'>
                {ETIQUETA_ESTADO[veredicto.estado]} · se omite
              </Badge>
            </Tooltip>
          )}
        </HStack>
      )}
    </Flex>
  );
};

/* ────────────────────────────────  modal  ──────────────────────────────── */

const RepetirReserva = ({ isOpen, onClose, reserva, codigoPendiente, onCreadas }: Props) => {
  const { user } = useAuth();
  const toast = useToast();

  const [salas, setSalas] = useState<Location[]>([]);
  const [cargandoSalas, setCargandoSalas] = useState(true);
  const [paso, setPaso] = useState<'configurar' | 'revisar' | 'listo'>('configurar');
  const [dias, setDias] = useState<number[]>([]);
  const [hasta, setHasta] = useState('');
  const [decisiones, setDecisiones] = useState<Record<string, Decision>>({});
  const [verDisponibles, setVerDisponibles] = useState(false);

  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creadas, setCreadas] = useState<{ code: string; fecha: string }[]>([]);

  const fechaBase = useMemo(() => {
    const t = reserva?.startDatetime?.toDate?.() ?? reserva?.datetime?.toDate?.();
    return t ?? new Date();
  }, [reserva]);

  const personas = Math.max(Number(reserva?.numberPeople ?? 1), 1);
  const salaBase = salas.find((s) => s.id === reserva?.location?.id);

  // Franjas que ocupa la reserva original: son las que ocuparán sus copias.
  const franjas = useMemo(
    () => (salaBase ? franjasQueOcupa(salaBase, claveFecha(fechaBase), reserva?.id ?? '') : []),
    [salaBase, fechaBase, reserva?.id]
  );

  useEffect(() => {
    if (!isOpen) return;
    setPaso('configurar');
    setDecisiones({});
    setVerDisponibles(false);
    setError(null);
    setCreadas([]);
    setDias([diaDeLaSemana(fechaBase)]);
    // Una semana por defecto: es lo más corto que ya tiene sentido, y alargarlo
    // es un clic. Un mes de entrada asustaba con 8 o 9 fechas de golpe.
    setHasta(format(addDays(fechaBase, 7), 'yyyy-MM-dd'));
  }, [isOpen, fechaBase]);

  /**
   * Trae las salas con su agenda. Se vuelve a llamar cuando una fecha se ocupa
   * mientras se revisaba: si no, la lista seguiría mostrando el estado viejo y
   * el siguiente intento fallaría igual.
   */
  const cargarSalas = useCallback(async () => {
    const empresa = reserva?.location?.company;
    if (!empresa) return;
    const empresaId = typeof empresa === 'string' ? empresa : empresa?.id;
    setCargandoSalas(true);
    try {
      const snap = await getDocs(
        query(
          collection(firebaseFirestore, 'locations'),
          where('company', '==', doc(firebaseFirestore, 'companies', empresaId)),
          where('status', '==', 'active')
        )
      );
      setSalas(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Location)));
    } finally {
      setCargandoSalas(false);
    }
  }, [reserva?.location?.company]);

  useEffect(() => {
    if (isOpen) cargarSalas();
  }, [isOpen, cargarSalas]);

  const fechas = useMemo(() => {
    if (!hasta) return [];
    const [a, m, d] = hasta.split('-').map(Number);
    return fechasDeLaSerie(fechaBase, dias, new Date(a, m - 1, d));
  }, [fechaBase, dias, hasta]);

  /** Evaluación de cada fecha: primero en su sala, y si no cabe, qué alternativas hay. */
  const filas: FilaFecha[] = useMemo(() => {
    if (paso === 'configurar' || !salaBase) return [];
    return fechas.map((fecha) => {
      const clave = claveFecha(fecha);
      const veredicto = evaluarFecha({ location: salaBase, fecha: clave, franjas, personas });
      return {
        clave,
        fecha,
        veredicto,
        alternativas:
          veredicto.estado === 'libre'
            ? []
            : salasAlternativas({
                salas,
                excluir: salaBase.id,
                fecha: clave,
                franjas,
                personas,
                comoLa: salaBase,
              }),
      };
    });
  }, [paso, fechas, salaBase, salas, franjas, personas]);

  const conflictos = filas.filter((f) => f.veredicto.estado !== 'libre');
  const disponibles = filas.filter((f) => f.veredicto.estado === 'libre');

  const decisionDe = (fila: FilaFecha): Decision =>
    decisiones[fila.clave] ??
    (fila.veredicto.estado === 'libre'
      ? { accion: 'crear', locationId: salaBase?.id ?? '' }
      : // Un choque nunca se resuelve solo: por defecto se omite, y quien
        // quiera moverlo de sala lo dice a mano.
        { accion: 'omitir', locationId: '' });

  const aCrear = filas.filter((f) => decisionDe(f).accion === 'crear');
  const aOmitir = filas.length - aCrear.length;
  /** Fechas libres que se quitaron a mano. */
  const quitadas = disponibles.filter((f) => decisionDe(f).accion === 'omitir').length;

  const revisar = () => {
    setError(null);
    if (!franjas.length) {
      setError(
        'Esta reserva no tiene horas ocupadas en la sala, así que no se puede repetir. ' +
          'Suele pasar con reservas muy antiguas.'
      );
      return;
    }
    setPaso('revisar');
  };

  const confirmar = async () => {
    setCreando(true);
    setError(null);
    try {
      const plan: FechaPlanificada[] = aCrear.map((f) => ({
        fecha: f.clave,
        locationId: decisionDe(f).locationId,
        franjas,
      }));

      const mapaSalas: Record<string, Location> = {};
      salas.forEach((s) => (mapaSalas[s.id] = s));

      const recurrenceId = `rec_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 7)}`;
      // Frase que se ve tal cual en el correo del cliente. Cuando la reserva del
      // día entra en la serie, la cuenta empieza en ella y no en la primera
      // repetición: para el cliente son todas sus reservas, no "las otras".
      const primera = codigoPendiente ? fechaBase : aCrear[0]?.fecha;
      const ultima = aCrear[aCrear.length - 1]?.fecha;
      const descripcion = `los ${enumerarDias(dias)}, del ${format(primera, "d 'de' MMMM", {
        locale: esLocale,
      })} al ${format(ultima, "d 'de' MMMM 'de' yyyy", { locale: esLocale })}`;

      const { creadas: hechas } = await crearSerie({
        base: reserva,
        baseId: reserva.id,
        fechas: plan,
        salas: mapaSalas,
        recurrenceId,
        descripcion,
        creadaPor: describirActor(user),
        // Siempre avisa, igual que una reserva normal: la diferencia es que
        // aquí el aviso es uno solo y cubre todas las fechas.
        avisarPorCorreo: true,
        codigoBasePendiente: codigoPendiente,
      });

      // Queda constancia en la reserva original de que dio pie a una serie.
      await registrarEvento({
        tipo: 'modified',
        reservationId: reserva.id,
        code: reserva.code,
        companyId: user?.company?.id,
        locationId: reserva.location?.id,
        locationName: reserva.location?.name,
        usuario: user,
        origen: { esPanel: true, from: 'sistema' },
        cambios: [
          {
            field: 'recurrencia',
            before: 'sin repetición',
            after: `${hechas.length} reservas · ${descripcion}`,
          },
        ],
        cliente: {
          nombre: reserva.namesAndSurnames ?? reserva.name,
          correo: reserva.email,
        },
      });

      setCreadas(hechas);
      setPaso('listo');
      onCreadas?.();
    } catch (e) {
      if (e instanceof FechaOcupadaError) {
        setError(
          `${fechaLarga(new Date(`${e.fecha}T00:00:00`))} a las ${e.hora} ya no hay sitio en ` +
            `${e.locationName ?? 'la sala'}. No se creó ninguna reserva; revisa las fechas otra vez.`
        );
        // Alguien reservó mientras tanto: hay que volver a mirar la agenda real.
        setDecisiones({});
        await cargarSalas();
        setPaso('revisar');
      } else {
        setError((e as Error).message || 'No se pudieron crear las reservas.');
      }
      toast({ status: 'error', duration: 6000, isClosable: true, title: 'No se creó nada' });
    } finally {
      setCreando(false);
    }
  };

  /**
   * Cerrar sin crear la serie no puede dejar la reserva a medias: se le escribe
   * el código que tenía apartado y a partir de ahí es una reserva normal, con
   * su correo de siempre. Da igual si se cierra con la X, con Escape o
   * pulsando fuera.
   */
  const cerrar = async () => {
    if (codigoPendiente && paso !== 'listo' && !creando) {
      try {
        const { updateReservation } = await import(
          '~/pages/public/Forms/Reservation/repositories/reservationRepository'
        );
        await updateReservation(reserva.id, { code: codigoPendiente });
        toast({
          status: 'info',
          duration: 5000,
          isClosable: true,
          title: 'La reserva queda sin repetir',
          description: `Se guardó como una reserva normal, con el código ${codigoPendiente}.`,
        });
        onCreadas?.();
      } catch {
        /* si no se puede, el aviso al usuario sigue siendo lo primero */
      }
    }
    onClose();
  };

  const limiteAlcanzado = fechas.length >= MAXIMO_FECHAS;
  const maximaFecha = format(addMonths(new Date(), MAXIMO_MESES), 'yyyy-MM-dd');

  return (
    <Modal isOpen={isOpen} onClose={cerrar} size='lg' isCentered scrollBehavior='inside'>
      <ModalOverlay backdropFilter='blur(2px)' />
      <ModalContent>
        <ModalHeader pb={2}>
          <HStack spacing={2}>
            <Icon as={FiRepeat} color='blue.500' />
            <Text fontSize='md'>Repetir reserva</Text>
          </HStack>
          <Text fontSize='sm' fontWeight='normal' color='gray.500' mt={1} noOfLines={2}>
            {reserva?.namesAndSurnames ?? reserva?.name} · {reserva?.location?.name} ·{' '}
            {reserva?.startHour}
            {reserva?.endHour && reserva.endHour !== '--' ? `–${reserva.endHour}` : ''} ·{' '}
            {personas} {personas === 1 ? 'persona' : 'personas'}
          </Text>
          <Text fontSize='xs' color='green.600' mt={1}>
            ✓ {fechaLarga(fechaBase)} ya queda reservado
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={creando} />

        <ModalBody pb={3}>
          {error && (
            <Alert status='error' borderRadius='md' fontSize='sm' mb={3}>
              <AlertIcon />
              {error}
            </Alert>
          )}

          {cargandoSalas ? (
            <Flex justify='center' py={10}>
              <Spinner />
            </Flex>
          ) : paso === 'configurar' ? (
            <VStack align='stretch' spacing={5}>
              <Box>
                <Text fontSize='sm' fontWeight='medium' mb={2}>
                  Se repite los
                </Text>
                <HStack spacing={1.5}>
                  {NOMBRES_DIAS.map((d) => {
                    const activo = dias.includes(d.indice);
                    return (
                      <Tooltip key={d.indice} label={capitalizar(d.largo)} fontSize='xs' hasArrow>
                        <Button
                          size='sm'
                          w='38px'
                          borderRadius='full'
                          colorScheme={activo ? 'blue' : 'gray'}
                          variant={activo ? 'solid' : 'outline'}
                          onClick={() =>
                            setDias((previos) =>
                              previos.includes(d.indice)
                                ? previos.filter((x) => x !== d.indice)
                                : [...previos, d.indice]
                            )
                          }
                        >
                          {d.corto}
                        </Button>
                      </Tooltip>
                    );
                  })}
                </HStack>
              </Box>

              <Box>
                <Text fontSize='sm' fontWeight='medium' mb={2}>
                  Hasta el
                </Text>
                <Input
                  type='date'
                  size='sm'
                  maxW='220px'
                  borderRadius='md'
                  value={hasta}
                  min={format(fechaBase, 'yyyy-MM-dd')}
                  max={maximaFecha}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </Box>

              <Box bg='gray.50' borderRadius='md' p={3}>
                {dias.length === 0 ? (
                  <Text fontSize='sm' color='gray.600'>
                    Elige al menos un día de la semana.
                  </Text>
                ) : fechas.length === 0 ? (
                  <Text fontSize='sm' color='gray.600'>
                    No hay ninguna fecha entre mañana y el día elegido.
                  </Text>
                ) : (
                  <>
                    <Text fontSize='sm'>
                      Se crearán <b>{fechas.length}</b>{' '}
                      {fechas.length === 1 ? 'reserva' : 'reservas'} más, cada{' '}
                      {enumerarDias(dias)}.
                    </Text>
                    <Text fontSize='xs' color='gray.500' mt={1}>
                      Además del día ya reservado. La primera repetición sería el{' '}
                      {fechaLarga(fechas[0]).toLowerCase()}.
                    </Text>
                  </>
                )}
                {limiteAlcanzado && (
                  <Text fontSize='xs' color='orange.600' mt={2}>
                    Es el máximo de {MAXIMO_FECHAS} repeticiones. Acorta la fecha final si
                    necesitas menos.
                  </Text>
                )}
              </Box>
            </VStack>
          ) : paso === 'revisar' ? (
            <VStack align='stretch' spacing={3}>
              <HStack spacing={4}>
                <HStack spacing={1.5}>
                  <Icon as={FiCheckCircle} color='green.500' boxSize={4} />
                  <Text fontSize='sm'>
                    <b>{disponibles.length}</b>{' '}
                    {disponibles.length === 1 ? 'disponible' : 'disponibles'}
                    {quitadas > 0 && (
                      <Text as='span' color='gray.500'>
                        {' '}
                        · {quitadas} {quitadas === 1 ? 'quitada' : 'quitadas'}
                      </Text>
                    )}
                  </Text>
                </HStack>
                {conflictos.length > 0 && (
                  <HStack spacing={1.5}>
                    <Icon as={FiAlertTriangle} color='orange.500' boxSize={4} />
                    <Text fontSize='sm'>
                      <b>{conflictos.length}</b>{' '}
                      {conflictos.length === 1 ? 'necesita' : 'necesitan'} tu decisión
                    </Text>
                  </HStack>
                )}
              </HStack>

              {conflictos.length > 0 && (
                <Box borderWidth='1px' borderColor='orange.200' borderRadius='md' overflow='hidden'>
                  {conflictos.map((fila) => (
                    <FilaRevision
                      key={fila.clave}
                      fila={fila}
                      decision={decisionDe(fila)}
                      tocada={Boolean(decisiones[fila.clave])}
                      salaBaseId={salaBase?.id ?? ''}
                      onDecidir={(d) =>
                        setDecisiones((previas) => ({ ...previas, [fila.clave]: d }))
                      }
                    />
                  ))}
                </Box>
              )}

              {disponibles.length > 0 && (
                <Box>
                  <Button
                    size='xs'
                    variant='link'
                    colorScheme='blue'
                    onClick={() => setVerDisponibles((v) => !v)}
                  >
                    {verDisponibles
                      ? 'Ocultar'
                      : disponibles.length === 1
                      ? 'Ver la fecha libre'
                      : `Ver las ${disponibles.length} fechas libres`}
                    {!verDisponibles && quitadas > 0 ? ` (${quitadas} quitada${quitadas === 1 ? '' : 's'})` : ''}
                  </Button>
                  <Collapse in={verDisponibles} animateOpacity>
                    <Text fontSize='xs' color='gray.500' mt={2}>
                      Si el cliente no necesita alguna, quítala con la ✕: esa no se creará.
                    </Text>
                    <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' mt={2}>
                      {disponibles.map((fila) => (
                        <FilaRevision
                          key={fila.clave}
                          fila={fila}
                          decision={decisionDe(fila)}
                          tocada
                          salaBaseId={salaBase?.id ?? ''}
                          onDecidir={(d) =>
                            setDecisiones((previas) => ({ ...previas, [fila.clave]: d }))
                          }
                        />
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {filas.length === 0 && (
                <Text fontSize='sm' color='gray.600'>
                  No hay fechas que revisar.
                </Text>
              )}

              <Divider />

              <HStack spacing={2} align='flex-start'>
                <Icon as={FiMail} color='gray.400' boxSize={4} mt='2px' />
                <Text fontSize='xs' color='gray.500'>
                  Al crearlas, el cliente recibe <b>un solo correo</b> con todas las fechas y un
                  enlace para verlas o cancelarlas.
                </Text>
              </HStack>
            </VStack>
          ) : (
            <VStack py={6} spacing={3}>
              <Box
                bg='green.50'
                borderRadius='full'
                w='56px'
                h='56px'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <Icon as={FiCheck} color='green.500' boxSize={7} />
              </Box>
              <Text fontWeight='semibold'>
                {creadas.length} {creadas.length === 1 ? 'reserva creada' : 'reservas creadas'}
              </Text>
              <Text fontSize='sm' color='gray.600' textAlign='center'>
                Cada una tiene su propio código y se puede cambiar o cancelar por separado.
              </Text>
              <Flex wrap='wrap' gap={1} justify='center' maxH='120px' overflowY='auto'>
                {creadas.map((c) => (
                  <Badge key={c.code} colorScheme='green' fontSize='0.7em'>
                    {c.code}
                  </Badge>
                ))}
              </Flex>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          {paso === 'configurar' && (
            <>
              <Button variant='ghost' size='sm' onClick={cerrar}>
                Cancelar
              </Button>
              <Button
                size='sm'
                colorScheme='blue'
                isDisabled={fechas.length === 0 || cargandoSalas}
                onClick={revisar}
              >
                Revisar disponibilidad
              </Button>
            </>
          )}
          {paso === 'revisar' && (
            <>
              <Button variant='ghost' size='sm' onClick={() => setPaso('configurar')}>
                Atrás
              </Button>
              <Button
                size='sm'
                colorScheme='blue'
                isLoading={creando}
                loadingText='Creando…'
                isDisabled={aCrear.length === 0}
                onClick={confirmar}
              >
                {aCrear.length === 0
                  ? 'Nada que crear'
                  : `Crear ${aCrear.length} ${aCrear.length === 1 ? 'reserva' : 'reservas'}`}
                {aOmitir > 0 ? ` · ${aOmitir} se ${aOmitir === 1 ? 'omite' : 'omiten'}` : ''}
              </Button>
            </>
          )}
          {paso === 'listo' && (
            <Button size='sm' colorScheme='blue' onClick={onClose}>
              Listo
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RepetirReserva;
